import draftToHtml from 'draftjs-to-html';
import { FormikHelpers, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import React from 'react';
import { useParams } from 'react-router-dom';
import { mixed, object } from 'yup';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Editor } from 'Component/Form';
import Imagem from 'Component/Imagem';
import Observacao from 'Interface/Observacoes';
import { useAdicionarObservacaoMutation } from 'View/Service/Default/Observacoes';
import {
  useEditarSolicitacaoMutation,
  useRecuperarSolicitacaoQuery,
} from 'View/Service/Default/Solicitacoes';

const regras = object({
  texto: mixed().test('obrigatorio', 'Obrigatório', (valor) => {
    if (valor?.blocks?.length && valor.blocks[0].text) {
      return true;
    }

    return false;
  }),
});

const Visualizar = (): React.ReactElement => {
  const { id = 0 } = useParams();
  const toast = useToast();
  const [adicionarObservacao] = useAdicionarObservacaoMutation();
  const [editarSolicitacao] = useEditarSolicitacaoMutation();

  const { data: { solicitacao } = {} } = useRecuperarSolicitacaoQuery({
    id: id,
    contain: ['Clientes', 'Atendentes', 'Observacoes.Usuarios'],
  });

  const enviar = async (
    observacao: Observacao,
    form: FormikHelpers<FormikValues>
  ): Promise<void> => {
    try {
      await adicionarObservacao({
        ...observacao,
        solicitacao_id: id as number,
      }).unwrap();

      toast({
        tema: 'success',
        conteudo: 'Observação adicionada com sucesso!',
      });

      form.resetForm();
    } catch (erro) {
      toast({
        titulo: 'Erro ao adicionar observação',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  const finalizar = async (): Promise<void> => {
    if (solicitacao) {
      try {
        await editarSolicitacao({
          id: solicitacao.id,
          status: 2,
        }).unwrap();

        toast({
          tema: 'success',
          conteudo: 'Solicitação finalizada com sucesso!',
        });
      } catch (erro) {
        toast({
          titulo: 'Erro ao finalizar a solicitação',
          conteudo: 'Verifique os logs para maiores detalhes.',
          tema: 'danger',
        });
      }
    }
  };

  return (
    <div className="animate__animated animate__fadeIn">
      <div
        className="hf-400 d-flex align-items-end pb-5"
        style={{
          backgroundImage:
            'linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0.5) 100%), url(/img/meus-cursos.jpg)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          marginTop: '-130px',
        }}
      >
        <div className="container text-center">
          <h1 className="display-2 text-white">
            Solicitação #{solicitacao?.id}
          </h1>
          <p className="lead text-muted">{solicitacao?.titulo}</p>
        </div>
      </div>
      <div className="mx-3">
        <div className="container mb-5">
          <div className="row justify-content-center">
            <div className="col-sm-3">
              <div className="bg-white rounded-3 p-3 position-relative">
                <div className="d-flex justify-content-center position-absolute top-0 start-50 translate-middle">
                  <Imagem
                    src={solicitacao?.atendente?.imagem}
                    width="100"
                    height="100"
                    className="rounded-circle object-fit-cover"
                  />
                </div>
                <p className="mt-5">
                  Atendimento por
                  <br />
                  <strong className="fs-4">
                    {solicitacao?.atendente?.nome || 'Aguardando atendimento'}
                  </strong>
                </p>
                {solicitacao && solicitacao?.status >= 1 && (
                  <p>
                    Iniciado em
                    <br />
                    <strong>
                      {solicitacao?.iniciado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </strong>
                  </p>
                )}
                {solicitacao?.status == 2 && (
                  <p className="mb-0">
                    Finalizado em
                    <br />
                    <strong>
                      {solicitacao.finalizado
                        .data()
                        .toLocaleString(DateTime.DATETIME_SHORT)}
                    </strong>
                  </p>
                )}
                {solicitacao?.status != 2 && (
                  <Botao
                    texto="Finalizar"
                    className="w-100"
                    onClick={finalizar}
                    confirmacao="Você tem certeza que deseja finalizar essa solicitação?"
                  />
                )}
              </div>
            </div>
            <div className="col-sm-8 mt-3 mt-sm-0">
              <div className="bg-white rounded-3 p-3 position-relative">
                {solicitacao && (
                  <div className="mb-1 bg-light rounded-3 p-3 border">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: draftToHtml(solicitacao.descricao),
                      }}
                    />
                    <small>
                      Por <strong>{solicitacao.cliente?.nome}</strong> as{' '}
                      <strong>
                        {solicitacao.criado
                          .data()
                          .toLocaleString(DateTime.DATETIME_SHORT)}
                      </strong>
                    </small>
                  </div>
                )}
                {solicitacao?.observacoes?.map((observacao) => (
                  <div
                    key={`${observacao.id}-${observacao.modificado}`}
                    className={`mb-1 rounded-3 p-3 ${
                      observacao.usuario_id == solicitacao.cliente_id
                        ? 'bg-light border ms-5'
                        : 'bg-primary bg-opacity-25 me-5'
                    }`}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: draftToHtml(observacao.texto),
                      }}
                    />
                    <small>
                      Por <strong>{observacao.usuario?.nome}</strong> as{' '}
                      <strong>
                        {observacao.criado
                          .data()
                          .toLocaleString(DateTime.DATETIME_SHORT)}
                      </strong>
                    </small>
                  </div>
                ))}
                <div className="mt-5 mb-n3 position-relative">
                  <Form
                    onSubmit={enviar}
                    regras={regras}
                    desabilitado={solicitacao?.status == 2}
                  >
                    <Editor
                      name="texto"
                      rotulo="Observações"
                      readOnly={solicitacao?.status == 2}
                    />
                    <Botao
                      lg
                      circulo
                      type="submit"
                      icone="send"
                      className="position-absolute top-0 end-0 translate-middle"
                    />
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Visualizar;
